import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Edit2, Trash2, Youtube, Repeat, Zap, Image as ImageIcon, Upload, ArrowRight, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { SubstitutePicker } from '../SubstitutePicker';
import { YoutubeLite, getYoutubeId } from './YoutubeLite';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => (url.startsWith('http') ? url : `${API}${url}`);

const ExerciseImageSlot = ({ exerciseId, slot, url, onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const label = slot === 'start' ? 'Imagem inicial' : 'Imagem final';

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Use JPG, PNG ou WebP');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Máximo 10MB');
      return;
    }
    const form = new FormData();
    form.append('file', file);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/api/admin/exercises/${exerciseId}/images/${slot}`, form);
      onChange(res.data);
      toast.success(`${label} atualizada!`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro no upload');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    try {
      const res = await axios.delete(`${API}/api/admin/exercises/${exerciseId}/images/${slot}`);
      onChange(res.data);
      toast.success(`${label} removida`);
    } catch (err) {
      toast.error('Erro ao remover');
    }
  };

  return (
    <div className="flex-1 min-w-0">
      <p className="text-[11px] uppercase tracking-wider font-bold text-zinc-500 mb-2">{label}</p>
      <div className="aspect-square rounded-xl bg-zinc-900 border border-zinc-800 overflow-hidden relative group">
        {url ? (
          <>
            <img src={resolveUrl(url)} alt={label} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-red-500 transition-all"
              title="Remover"
              data-testid={`exercise-image-remove-${slot}-${exerciseId}`}
            >
              <X className="w-3.5 h-3.5 text-white" />
            </button>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-8 h-8 text-zinc-700" />
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFile} className="hidden" />
      <Button
        type="button"
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="w-full mt-2 bg-neon-lime text-black font-bold hover:bg-neon-lime/80 h-8 text-xs"
        data-testid={`exercise-image-upload-${slot}-${exerciseId}`}
      >
        <Upload className="w-3 h-3 mr-1.5" />
        {uploading ? 'Enviando...' : (url ? 'Trocar' : 'Enviar')}
      </Button>
    </div>
  );
};

const ExerciseEditForm = ({ ex, options, onSave, onCancel, onImageChange }) => {
  const [form, setForm] = useState({
    name: ex.name,
    category: ex.category || '',
    youtube_url: ex.youtube_url || '',
    instructions: ex.instructions || '',
  });
  const [substituteIds, setSubstituteIds] = useState(ex.substitute_ids || []);
  const [saving, setSaving] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Nome do exercício é obrigatório'); return; }
    setSaving(true);
    await onSave(ex.id, {
      name: form.name.trim(),
      category: form.category || null,
      youtube_url: form.youtube_url || null,
      instructions: form.instructions || null,
      substitute_ids: substituteIds,
    });
    setSaving(false);
  };

  return (
    <div className="space-y-3" data-testid={`exercise-edit-form-${ex.id}`}>
      <Input value={form.name} onChange={set('name')} placeholder="Nome" className="bg-zinc-800 border-zinc-700 text-white" data-testid="edit-exercise-name" />
      <Input value={form.category} onChange={set('category')} placeholder="Categoria" className="bg-zinc-800 border-zinc-700 text-white" list="category-suggestions" data-testid="edit-exercise-category" />
      <Input value={form.youtube_url} onChange={set('youtube_url')} placeholder="Link YouTube" className="bg-zinc-800 border-zinc-700 text-white" data-testid="edit-exercise-youtube" />
      <textarea value={form.instructions} onChange={set('instructions')} placeholder="Instruções" className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg p-3 min-h-[60px]" data-testid="edit-exercise-instructions" />
      <SubstitutePicker selectedIds={substituteIds} onChange={setSubstituteIds} library={options} currentExerciseName={ex.name} />
      <div>
        <p className="text-sm text-zinc-300 mb-2 font-medium">Imagens do movimento</p>
        <div className="flex items-stretch gap-2">
          <ExerciseImageSlot exerciseId={ex.id} slot="start" url={ex.start_image_url} onChange={onImageChange} />
          <div className="flex items-center justify-center text-neon-lime/60 pt-6">
            <ArrowRight className="w-5 h-5" />
          </div>
          <ExerciseImageSlot exerciseId={ex.id} slot="end" url={ex.end_image_url} onChange={onImageChange} />
        </div>
        <p className="text-[11px] text-zinc-500 italic mt-2">
          As duas imagens aparecem automaticamente em todos os treinos que usam esse exercício.
        </p>
      </div>
      <p className="text-[11px] text-zinc-500 italic flex items-center gap-1.5">
        <Zap className="w-3 h-3 text-neon-purple" />
        Substitutos são aplicados automaticamente em todos os planos ao salvar.
      </p>
      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={saving} className="bg-neon-lime text-black" data-testid={`exercise-save-${ex.id}`}>
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} className="border-zinc-700 text-zinc-400" data-testid={`exercise-cancel-${ex.id}`}>
          Cancelar
        </Button>
      </div>
    </div>
  );
};

const ExerciseView = ({ ex, onEdit, onDelete }) => (
  <div>
    <div className="flex items-start justify-between gap-4 mb-3">
      <div className="flex-1 min-w-0">
        <h4 className="font-bold text-white text-lg" data-testid={`exercise-title-${ex.id}`}>{ex.name}</h4>
        {ex.category && (
          <span className="inline-block text-xs bg-neon-purple/20 text-neon-purple px-2 py-1 rounded-full mt-1">{ex.category}</span>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" onClick={onEdit} className="text-zinc-400 hover:text-neon-lime p-2" data-testid={`exercise-edit-${ex.id}`}>
          <Edit2 className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete} className="text-zinc-400 hover:text-red-500 p-2" data-testid={`exercise-delete-${ex.id}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>

    {ex.youtube_url && <YoutubeLite url={ex.youtube_url} title={ex.name} testId={`exercise-video-${ex.id}`} />}
    {ex.youtube_url && !getYoutubeId(ex.youtube_url) && (
      <div className="flex items-center gap-2 text-red-400 text-sm mb-3">
        <Youtube className="w-4 h-4" />
        <span className="truncate">{ex.youtube_url}</span>
      </div>
    )}

    {ex.start_image_url && ex.end_image_url && (
      <div className="flex items-center gap-2 mb-3">
        <img src={resolveUrl(ex.start_image_url)} alt="Posição inicial" loading="lazy" className="flex-1 w-0 aspect-square object-cover rounded-lg border border-white/5" />
        <ArrowRight className="w-4 h-4 text-neon-lime flex-shrink-0" />
        <img src={resolveUrl(ex.end_image_url)} alt="Posição final" loading="lazy" className="flex-1 w-0 aspect-square object-cover rounded-lg border border-white/5" />
      </div>
    )}

    {ex.instructions && <p className="text-zinc-400 text-sm line-clamp-3">{ex.instructions}</p>}

    {ex.substitute_ids?.length > 0 && (
      <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-white/5 text-xs">
        <Repeat className="w-3.5 h-3.5 text-neon-lime" />
        <span className="text-zinc-400">{ex.substitute_ids.length} substituto(s) cadastrado(s)</span>
      </div>
    )}
  </div>
);

export const ExerciseCard = ({ ex, editing, highlighted, options, onStartEdit, onCancelEdit, onSave, onDelete, onItemChange }) => (
  <motion.div
    className={`bg-zinc-950/50 border rounded-xl p-4 transition-colors duration-700 ${
      highlighted ? 'border-neon-lime shadow-[0_0_24px_rgba(204,255,0,0.25)]' : 'border-white/5'
    }`}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    data-testid={`exercise-card-${ex.id}`}
  >
    {editing ? (
      <ExerciseEditForm ex={ex} options={options} onSave={onSave} onCancel={onCancelEdit} onImageChange={onItemChange} />
    ) : (
      <ExerciseView ex={ex} onEdit={onStartEdit} onDelete={onDelete} />
    )}
  </motion.div>
);
